import { KeyValue } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  ElementRef,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { MatBottomSheet, MatBottomSheetConfig, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatSidenav } from '@angular/material/sidenav';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipDefaultOptions, MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { AbcCore, DotNetBridge } from '@barthauer/abc/core';
import { DataRowInfo, ObjectKey } from '@barthauer/abc/core/data';
import { catchErrorAndRethrow } from '@barthauer/abc/core/error-handling';
import { ResourceManagerService } from '@barthauer/abc/core/resources';
import { BuildInfoService } from '@barthauer/abc/core/setup-info';
import { DataEntity, MapActionArgument, MapActionIds } from '@barthauer/abc/graphics/adapters/open-layers';
import {
  AbcAutoCompleteInputComponent,
  AutoCompletePanelBehaviour,
  AbcPopupComponent,
  AbcTabPanelComponent,
  _AbcSplitterComponent,
} from '@barthauer/abc/ui-components';
import { LoginInfo, ProfileInfo } from '@barthauer/basys/core/authentication';
import 'hammerjs';
import { ResourceSet } from 'libs/abc/core/resources/src/lib/resource-set';
import { Observable, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BasysMapFrameService, ObjectTypeWithFilter } from './basys-map-frame.service';
import { BasysMapFrameConfiguration, WidgetConfiguration } from './models/config.model';
import { CreateGeoObjectInfo, MapActionError, ObjectTypeWithGeoDefsCandidate } from './models/mapsData.model';
import { CaptionStyle, Setting } from './models/setting.model';
import { ViewStateChangeArgType } from './models/viewState.model';
import { LibService } from './services/lib.service';
import { RoutingService } from './services/routing.service';
import { ContainerComponent } from './shared/container.component';
import { TableComponent } from './table/table.component';
import { MapInfoGeometryDefinitionViewModel, MapInfoObjectTypeViewModel } from './viewModels/mapsData.viewModel';
import { AbcAutoCompleteWithCoordsComponent } from 'libs/abc/ui-components/src/lib/base/src/abcAutoCompleteInputWithCoords/abcAutoCompleteInputWithCoords.component';
import { MatMenuTrigger } from '@angular/material/menu';
import { Clipboard } from '@angular/cdk/clipboard';

export interface SystemVersionInfo {
  version: string;
  fullVersion: string;
  buildDate: string;
}

export const tooltipDefaults: MatTooltipDefaultOptions = {
  showDelay: 1000,
  hideDelay: 100,
  touchendHideDelay: 100,
  disableTooltipInteractivity: true,
};

@Component({
  selector: 'basys-map-frame',
  templateUrl: './basys-map-frame.component.html',
  styleUrls: ['./basys-map-frame.component.css'],
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: tooltipDefaults }],
  standalone: false,
})
export class BasysMapFrameComponent implements OnInit, AfterViewInit, OnDestroy {
  Object = Object; //ah, its a dream ;-) love JS *vomiting*
  console = console;
  CaptionStyle = CaptionStyle;
  sideNavStillOpened: boolean;
  mapInfosLoaded = false;
  private isBottomSheetOpen: boolean;
  private currentBottomSheetWidget;
  private hammer;
  private bottomSheetRef: MatBottomSheetRef;
  private bottomSheetElement;
  private bottomSheetHammerEl;
  private startPos: number = 0;
  private startHeight: number = 0;
  private isFirstTimeOpenBottomSheet: boolean = true;
  private screenHeight: number;
  private dxToolBar: HTMLElement;
  private cdkOverlay: HTMLElement;
  private readonly cdkOverlayBehindBottomBarZIndex: string = '998';
  private divergentExternalZIndex: string;
  private subscriptions: Subscription[] = [];
  public newTopBottomSheet: number;
  loginInfoObservable$: Observable<LoginInfo>;
  profileInfoObservable$: Observable<ProfileInfo>;
  frameConfigObservable$: Observable<BasysMapFrameConfiguration>;
  allAutocompleteItems: string[];
  allAutocompleteItemObjectKeys: Map<string, ObjectKey[]>;
  allAutocompleteItemDataRows: Map<string, DataRowInfo[]>;
  AutoCompletePanelBehaviour = AutoCompletePanelBehaviour;
  private destroy$ = new Subject<void>(); // used to cancel Observable chains when component is destroyed
  isMenuOpened: boolean = false;
  isMobileEnv: boolean = false;
  showSystemInfo = false;
  systemVersionInfo: SystemVersionInfo = {
    version: '',
    fullVersion: '',
    buildDate: '',
  };

  @ViewChild('hiddenComponentViewContainer', {
    static: true,
    read: ViewContainerRef,
  })
  hiddenComponentViewContainer: ViewContainerRef;
  @ViewChild('sideNav', { static: true }) sideNav: MatSidenav;
  @ViewChild('abcAutoCompleteInput', { static: true })
  abcAutoCompleteInput: AbcAutoCompleteWithCoordsComponent;

  @ViewChild('splitter', { static: true }) splitter: _AbcSplitterComponent;
  @ViewChild('menuTrigger') menuTrigger!: MatMenuTrigger;

  public res = ResourceSet.Empty;

  constructor(
    private bottomSheet: MatBottomSheet,
    private resourceService: ResourceManagerService,
    public frame: BasysMapFrameService,
    private snackBar: MatSnackBar,
    private routingService: RoutingService,
    private abcCore: AbcCore,
    private cd: ChangeDetectorRef,
    public lib: LibService,
    private renderer: Renderer2,
    private el: ElementRef,
    private buildInfoService: BuildInfoService,
    private clipboard: Clipboard,
  ) {
    this.loadLoginInfo(false);

    this.profileInfoObservable$ = this.frame
      .getCurrentProfile()
      .pipe(catchErrorAndRethrow((err) => this.abcCore.error.publish(err)));

    this.frameConfigObservable$ = this.frame
      .getCurrentConfig()
      .pipe(catchErrorAndRethrow((err) => this.abcCore.error.publish(err)));

    this.frame.detectChanges = () => this.cd.detectChanges();

    lib.isBottomSheetOpenByName = (name) => {
      const widget = this.frame.getWidget(name);
      if (this.currentBottomSheetWidget === widget && this.isBottomSheetOpen) {
        return true;
      }
      return false;
    };

    frame.initSearchValues = async () => await this.initSearchValues(true);
  }

  private loadLoginInfo(doRefresh?: boolean) {
    this.loginInfoObservable$ = this.frame
      .getLoginInfo(doRefresh)
      .pipe(catchErrorAndRethrow((err) => this.abcCore.error.publish(err)));
  }

  public getSortedSettings(settings: Setting[]): Setting[] {
    return settings.sort((s1, s2) => {
      if (s1.sortOrder > s2.sortOrder) {
        return 1;
      } else if (s1.sortOrder < s2.sortOrder) {
        return -1;
      } else {
        return 0;
      }
    });
  }

  ngOnDestroy() {
    console.log('frame_destroy');
    this.frame.destroyObjectInfo.next(null);
    this.bottomSheet.dismiss();
    this.subscriptions.forEach((element) => {
      element.unsubscribe();
    });

    this.destroy$.next(null); // trigger the unsubscribe
    this.destroy$.complete(); // finalize & clean up the subject stream
  }

  async ngOnInit() {
    this.res = await this.resourceService.getResourceSet('basys-map-frame');
    this.systemVersionInfo = {
      version: this.buildInfoService.getMainVersion(),
      fullVersion: this.buildInfoService.getFullVersion(),
      buildDate: this.buildInfoService.getBuildDate(),
    };

    this.frame.getMapInfosStatus().subscribe(
      (mi) => {
        this.mapInfosLoaded = mi;
        this.initSearchValues(false);
      },
      (error) => {
        this.abcCore.error.publish(error);
      },
    );

    this.frame?.mainMenuClose$.subscribe(() => this.closeMenu());
  }

  get areProfilesSupported(): boolean {
    return this.frame.areProfilesSupported;
  }

  canLogout(): boolean {
    return this.frame.canChangeProfileOrLogout();
  }

  openSystemInfoComponent(): void {
    this.showSystemInfo = true;
    this.cd.detectChanges();
  }

  closeSystemInfo(): void {
    this.showSystemInfo = false;
    this.cd.detectChanges();
  }

  copyToClipboard(text: string): void {
    this.clipboard.copy(text);
  }

  canSelectProfile(): boolean {
    return this.frame.canChangeProfileOrLogout();
  }

  selectProfile(): void {
    this.bottomSheet.dismiss();
    this.frame
      .selectProfile('/')
      .pipe(takeUntil(this.destroy$))
      .subscribe({ error: (err) => this.abcCore.error.publish(err) });
  }

  openUserSettingsComponent(): void {
    this.frame.lib.openUserSettingsComponent(() => this.loadLoginInfo(true));
  }

  openHelp() {
    this.abcCore.openWindow(this.frame.frameConfig.helpUrl);
  }

  logout(removeCurrentProfile: boolean = false): void {
    this.frame
      .logout(removeCurrentProfile)
      .pipe(takeUntil(this.destroy$))
      .subscribe({ error: (err) => this.abcCore.error.publish(err) });
  }

  globalSearch(input?: string) {
    if (!input) {
      let searchValue: string = '';

      if (typeof this.abcAutoCompleteInput.value === 'string') {
        searchValue = this.abcAutoCompleteInput.value;
      } else {
        const coordinateOption: any = this.abcAutoCompleteInput.value;
        if (coordinateOption) {
          searchValue = coordinateOption.label;
        }
      }
      input = searchValue;
    } else {
      if (typeof input !== 'string') {
        console.warn('globalSearch received non-string input, ignoring:', input);
        return;
      }
      input = input.toLowerCase();
    }

    let keys = this.allAutocompleteItemObjectKeys.get(input);
    if (!keys) {
      this.allAutocompleteItemObjectKeys.forEach((v, k) => {
        if (k.startsWith(input)) {
          if (!keys) keys = [];
          v.forEach((_) => keys.push(_));
        }
      });
    }
    if (keys) {
      const entities = keys.map((_) => new DataEntity(_.ObjectType, _.Id));
      this.frame.map.navigateToObjects(entities);
    } else {
      let rows = this.allAutocompleteItemDataRows.get(input);
      if (!rows) {
        this.allAutocompleteItemDataRows.forEach((v, k) => {
          if (k.startsWith(input)) {
            if (!rows) rows = [];
            v.forEach((_) => rows.push(_));
          }
        });
      }
      if (rows) {
        this.frame.map.navigateToDataRows(rows);
      }
    }
  }

  onCoordinateSelected(option: any) {
    this.frame.map.markCoordinate(option?.value.latOrX, option?.value.lonOrY, option.type === 'projected');
  }

  onClearCoordinateMarkers() {
    this.frame.map.clearCoordinateMarkers();
  }

  ngAfterViewInit() {
    // this.renderer.appendChild(document.body, this.el.nativeElement.querySelector('.bottomBar'));
    this.subscriptions.push(
      this.frame.map.onMapAction().subscribe(
        (mapActionArgument) => {
          try {
            if (!mapActionArgument || !mapActionArgument.actionId) {
              throw new MapActionError('No or invalid MapAction argument supplied.');
            }

            switch (mapActionArgument.actionId) {
              case MapActionIds.CREATE_OBJECT: {
                this.selectObjectTypeAndGeoDefAndContinue(
                  mapActionArgument,
                  (gDef) => gDef.canAdd,
                  (info) => {
                    this.frame.openNewObjectDialog(
                      new DataEntity(info.objectTypeName, null, mapActionArgument.feature),
                      info.geoIdentifier,
                    );
                  },
                );
                break;
              }
              case MapActionIds.CREATE_TICKET:
              case MapActionIds.MARK_COORDINATE:
              case MapActionIds.CLEAR_MARKERS:
              case MapActionIds.CLEAR_SELECTIONS: {
                //Handling in the app;
                break;
              }

              default: {
                throw new MapActionError("Action '" + mapActionArgument.actionId + "' is not implemented!");
              }
            }
          } catch (error) {
            if (error instanceof MapActionError) {
              this.openSnackBar("MapAction-Fehler:'" + error.message, 5000);
            } else {
              throw error;
            }
          }
        },
        (error) => {
          this.abcCore.error.publish(error);
        },
      ),
    );

    this.subscriptions.push(
      this.frame.map.onMapShortMessageReceived().subscribe(
        (message) => {
          this.openSnackBar(message);
        },
        (error) => {
          this.abcCore.error.publish(error);
        },
      ),
    );

    this.subscriptions.push(
      this.frame.lib.onViewStateChanged().subscribe(
        (args) => {
          switch (args.type) {
            case ViewStateChangeArgType.ClientPanStart: {
              if (this.hammer) {
                this.hammer.stop(true);
              }
              break;
            }
            case ViewStateChangeArgType.ClientPanEnd: {
              if (this.hammer) {
                this.hammer.on('pandown panup', (event: any) => {
                  this.bottomSheetPanMove(event);
                });
              }
              break;
            }
          }
        },
        (error) => {
          this.abcCore.error.publish(error);
        },
      ),
    );

    this.subscriptions.push(
      this.frame.lib.onBottomSheetOpenDemand().subscribe(
        (widget) => this.toggleBottomSheet(widget),
        (error) => {
          this.abcCore.error.publish(error);
        },
      ),
    );
    this.subscriptions.push(
      this.frame.lib.onBottomSheetCloseDemand().subscribe(
        async () => {
          if (await this.frame.isBottomSheetToggleAllowed()) {
            this.dismissBottomSheet();
          }
        },
        (error) => {
          this.abcCore.error.publish(error);
        },
      ),
    );

    this.subscriptions.push(
      this.frame.onOpenDataTable().subscribe(
        function (ot: ObjectTypeWithFilter) {
          if (
            !this.isBottomSheetOpen ||
            !this.bottomSheetRef ||
            this.currentBottomSheetWidget.name != BasysMapFrameService.Widget_Table
          ) {
            setTimeout(() => {
              TableComponent.setNextObjectTypeValue(ot);
              const widget: TableComponent = this.frame.getWidget(BasysMapFrameService.Widget_Table);
              if (widget && (!this.isBottomSheetOpen || this.currentBottomSheetWidget !== widget)) {
                this.toggleBottomSheet(widget);
              }
            }, 100);
          }
        }.bind(this),
      ),
    );

    this.subscriptions.push(
      this.frame.map.onMobileEnvironment().subscribe((isMobile) => {
        this.isMobileEnv = isMobile;
      }),
    );
  }

  private async initSearchValues(executeQuery: boolean): Promise<void> {
    const el = this.abcAutoCompleteInput?.searchInput?.nativeElement;
    if (el) {
      el.disabled = true;
    }

    if (executeQuery) {
      let errorCounter: number = 0;
      if (!DotNetBridge.hasContext) {
        while (true) {
          try {
            await this.initSearchValuesCore(el);
            break;
          } catch (err) {
            if (errorCounter++ > 2) {
              this.abcCore.error.publish(err);
              break;
            }
          }
        }
      }
      this.cd.markForCheck(); // or this.cd.detectChanges() to enforce refresh
    }
  }

  private async initSearchValuesCore(el: any): Promise<void> {
    const values = await this.frame.queryMapObjectValues(null).toPromise();
    el.disabled = false;
    this.allAutocompleteItems = values.map((_) => _.Value);
    this.allAutocompleteItemObjectKeys = new Map<string, ObjectKey[]>();
    for (let v of values) {
      if (v.Objects?.length) {
        this.allAutocompleteItemObjectKeys.set(v.Value?.toLowerCase(), v.Objects);
      }
    }
    this.allAutocompleteItemDataRows = new Map<string, DataRowInfo[]>();
    for (let v of values) {
      if (v.DataRows?.length) {
        this.allAutocompleteItemDataRows.set(v.Value?.toLowerCase(), v.DataRows);
      }
    }
  }

  private selectObjectTypeAndGeoDefAndContinue(
    mapActionArgument: MapActionArgument,
    geoDefPredicate: (gDef: MapInfoGeometryDefinitionViewModel) => boolean,
    continueIfOk: (info: CreateGeoObjectInfo) => void,
    continueIfCancelled: () => void = null,
  ) {
    const geoType: string = mapActionArgument.feature?.getGeometry()?.getType();
    const isNewGeoConstruction: boolean = mapActionArgument.isFirstConstructionAction ?? true;

    if (isNewGeoConstruction) {
      this.currentCreateGeoObjectInfo = null;
    }

    if (!geoType) {
      throw new MapActionError('The supplied MapAction argument does not contain a geometry type!');
    }

    let findGeometryDefs: (ot: MapInfoObjectTypeViewModel) => MapInfoGeometryDefinitionViewModel[] = null;

    // Map Action argument has GeometryType
    if (geoType.indexOf('Point') >= 0) {
      findGeometryDefs = (ot) => ot.points; // Debugging: .concat(ot.polygons).concat(ot.lines);
    } else if (geoType.indexOf('Polygon') >= 0) {
      findGeometryDefs = (ot) => ot.polygons;
    } else if (geoType.indexOf('Line') >= 0) {
      findGeometryDefs = (ot) => ot.lines;
    } else {
      throw new MapActionError(
        'The geometry type supplied by the MapAction argument is invalid (not Line, Point or Polygon)!',
      );
    }

    // GeometryType is valid
    const findSpecialGeometryDefs: (ot: MapInfoObjectTypeViewModel) => MapInfoGeometryDefinitionViewModel[] = (ot) =>
      findGeometryDefs(ot).filter((gDef) => geoDefPredicate(gDef));
    const otCandidates: ObjectTypeWithGeoDefsCandidate[] = this.frame.mapInfo.objectTypes
      .map(
        (info) =>
          new ObjectTypeWithGeoDefsCandidate({
            objectType: info,
            allowedGeoDefinitions: findSpecialGeometryDefs(info),
          }),
      )
      .filter((c) => c.allowedGeoDefinitions.length > 0);

    if (!otCandidates || otCandidates.length === 0) {
      throw new MapActionError(
        'No ObjectType available that supports a geometry which fulfills the requirements of the geometry type supplied by the MapAction argument!',
      );
    }

    this.frame
      .buildSelectObjectTypeAndGeoDefIdList(otCandidates)
      .then((objectTypeAndGeoDefIdListItems) => {
        if (!objectTypeAndGeoDefIdListItems || objectTypeAndGeoDefIdListItems.length === 0) {
          throw new MapActionError(
            'No ObjectType available that supports a geometry which fulfills the requirements of the geometry type supplied by the MapAction argument!',
          );
        }

        let mustShowDialog = true;

        if (
          this.currentCreateGeoObjectInfo &&
          this.currentCreateGeoObjectInfo.objectTypeName &&
          this.currentCreateGeoObjectInfo.geoIdentifier &&
          objectTypeAndGeoDefIdListItems.some(
            (item) =>
              item.concreteObjectTypeName === this.currentCreateGeoObjectInfo.objectTypeName &&
              item.geoIdentifiers.includes(this.currentCreateGeoObjectInfo.geoIdentifier),
          )
        ) {
          mustShowDialog = false;
        } else if (
          objectTypeAndGeoDefIdListItems.length === 1 &&
          objectTypeAndGeoDefIdListItems[0].geoIdentifiers.length === 1
        ) {
          this.currentCreateGeoObjectInfo = new CreateGeoObjectInfo(<CreateGeoObjectInfo>{
            objectTypeName: objectTypeAndGeoDefIdListItems[0].concreteObjectTypeName,
            geoIdentifier: objectTypeAndGeoDefIdListItems[0].geoIdentifiers[0],
          });

          mustShowDialog = false;
        }

        if (mustShowDialog) {
          this.frame.openSelectObjectTypeAndGeoDefDialog(
            objectTypeAndGeoDefIdListItems,
            (info) => {
              this.currentCreateGeoObjectInfo = info;
              this.openSnackBar(this.res.get('CreateGeoObjectInfo', info.objectTypeName, info.geoIdentifier), 2000);
              continueIfOk(info);
            },
            continueIfCancelled,
          );
        } else {
          this.openSnackBar(
            this.res.get(
              'CreateGeoObjectInfo',
              this.currentCreateGeoObjectInfo.objectTypeName,
              this.currentCreateGeoObjectInfo.geoIdentifier,
            ),
            2000,
          );
          continueIfOk(this.currentCreateGeoObjectInfo);
        }
      })
      .catch((reason) => {
        if (reason instanceof MapActionError) {
          this.openSnackBar(this.res.get('MapActionError', reason.message), 5000);
        } else {
          this.abcCore.error.publish(reason);
        }

        if (continueIfCancelled) {
          continueIfCancelled();
        }
      });
  }

  private currentCreateGeoObjectInfo: CreateGeoObjectInfo = null;

  openSnackBar(text: string, duration: number = 2000) {
    setTimeout(() => {
      this.snackBar.open(text, null, {
        duration: duration <= 0 ? 2000 : duration,
        verticalPosition: 'top',
      });
    }, 1);
  }

  async toggleBottomSheet(widget: WidgetConfiguration): Promise<void> {
    if (await this.frame.isBottomSheetToggleAllowed()) {
      if (!this.isBottomSheetOpen || !this.bottomSheetRef) {
        this.openBottomSheetCore(widget);
        this.isBottomSheetOpen = true;
      } else if (this.currentBottomSheetWidget === widget) {
        this.dismissBottomSheet();
        this.isBottomSheetOpen = false;
        this.lib.closeBottomSheet();
      } else {
        const bottomSheetIsHidden = (bs) => {
          return new Promise((resolve, reject) => {
            if (bs.containerInstance?._animationState === 'hidden') {
              resolve({});
            } else {
              //this.router.navigate(['/']);
              bs.containerInstance?._animationStateChanged.subscribe((e) => {
                if (e.toState === 'hidden' && e.phase === 'start') {
                  setTimeout(() => {
                    resolve({});
                  }, e.totalTime - 100);
                }
              });
              bs.dismiss();
            }
          });
        };

        bottomSheetIsHidden(this.bottomSheetRef).then(() => {
          this.dismissBottomSheet();
          this.openBottomSheetCore(widget);
        });

        this.subscriptions.push(
          this.bottomSheetRef.backdropClick().subscribe(
            () => {
              this.isBottomSheetOpen = false;
            },
            (error) => {
              this.abcCore.error.publish(error);
            },
          ),
        );
      }
    }
  }

  async openBottomSheetCore(widget: WidgetConfiguration): Promise<void> {
    const options: MatBottomSheetConfig = new MatBottomSheetConfig();
    options.panelClass = 'bottom-sheet-container';
    options.backdropClass = 'bottom-sheet-backdrop';
    options.hasBackdrop = this.frame.frameConfig.hasBackdrop;

    this.bottomSheetRef = this.bottomSheet.open(ContainerComponent, options);
    this.ensureBottomSheetElement();

    const componentToShow: ComponentRef<unknown> = this.frame.ensureWidgetComponent(widget);
    this.bottomSheetRef.instance.insert(componentToShow);

    this.currentBottomSheetWidget = widget;
    this.newTopBottomSheet = 0;

    this.subscriptions.push(
      this.bottomSheetRef.containerInstance?._animationStateChanged.subscribe(
        (e) => {
          this.cdkOverlay = document.querySelector('.cdk-overlay-container') as HTMLElement;
          if (e.toState === 'visible' && (e.phase === 'start' || (e.phase === 'done' && !this.bottomSheetElement))) {
            this.setWidgetBehindBottomBar();
            this.ensureBottomSheetElement();

            this.frame.lib.raiseViewStateChanged({
              type: ViewStateChangeArgType.BottomSheetPanelOpening,
            });
            setTimeout(() => {
              this.frame.detectChanges();
            }, 100);
          } else if (e.toState === 'hidden' && e.phase === 'start') {
            this.setWidgetBehindBottomBar();
            this.frame.lib.raiseViewStateChanged({
              type: ViewStateChangeArgType.BottomSheetPanelClosing,
            });
            this.bottomSheetRef.instance.clean();

            // setTimeout(() => {
            ////  this.bottomSheetRef.instance.clean();
            // },  100);
          } else if (e.toState === 'hidden' && e.phase === 'done') {
            this.resetWidgetToFront();
            this.isBottomSheetOpen = false;
          }
          if (e.toState === 'visible' && e.phase === 'done' && this.bottomSheetElement) {
            this.resetWidgetToFront();
            this.frame.lib.raiseViewStateChanged({
              type: ViewStateChangeArgType.BottomSheetPanelOpened,
              bottomSheetPanelHeight: this.bottomSheetElement.clientHeight,
            });
          }
        },
        (error) => {
          this.abcCore.error.publish(error);
        },
      ),
    );

    this.subscriptions.push(
      this.bottomSheetRef.afterOpened().subscribe(
        () => {
          if (document.querySelector('.toolbar-style')) {
            this.bottomSheetHammerEl = document.querySelector('.toolbar-style');
            if (this.bottomSheetHammerEl) {
              this.hammer = new Hammer(this.bottomSheetHammerEl);
              this.hammer.get('pan').set({ direction: Hammer.DIRECTION_VERTICAL });
              this.hammer.on('panstart', (event: any) => {
                this.bottomSheetPanStart(event);
              });
              this.hammer.on('pandown panup', (event: any) => {
                this.bottomSheetPanMove(event);
              });
              this.isBottomSheetOpen = true;
            }
          }
        },
        (error) => {
          this.abcCore.error.publish(error);
        },
        null,
      ),
    );

    if (widget.route) {
      await this.routingService.navigate(widget.route, true);
    }
  }

  private ensureBottomSheetElement() {
    this.bottomSheetElement =
      document.querySelector('.bottom-sheet-container > .mat-bottom-sheet-container') ?? this.bottomSheetElement;
    this.dxToolBar =
      ((document.querySelector('.devExToolbarStyle') as HTMLElement) ||
        (document.querySelector('abc-toolbar') as HTMLElement)) ??
      this.dxToolBar;
    let bottomPos = 63;
    if (this.dxToolBar) {
      bottomPos += this.dxToolBar.offsetHeight;
    }
    this.bottomSheetElement?.style?.setProperty(
      'max-height',
      'calc(' + this.frame.frameConfig.bottomSheetHeightPercentage + '% - ' + bottomPos + 'px)',
      'important',
    );

    this.bottomSheetElement?.style?.setProperty(
      'min-height',
      'calc(' + this.frame.frameConfig.bottomSheetHeightPercentage + '% - ' + bottomPos + 'px)',
      'important',
    );
  }

  private setWidgetBehindBottomBar(): void {
    const zIndex: string = this.cdkOverlay.style.getPropertyValue('z-index');
    if (zIndex !== this.cdkOverlayBehindBottomBarZIndex) {
      this.divergentExternalZIndex = zIndex;
    }
    this.cdkOverlay.style.setProperty('z-index', this.cdkOverlayBehindBottomBarZIndex, 'important');
  }

  private resetWidgetToFront(): void {
    if (this.divergentExternalZIndex !== '') {
      this.cdkOverlay.style.setProperty('z-index', this.divergentExternalZIndex, 'important');
    } else {
      this.cdkOverlay.style.removeProperty('z-index');
    }
  }

  private dismissBottomSheet() {
    try {
      this.isFirstTimeOpenBottomSheet = true;
      this.hammer.off('pan');
      this.hammer.off('panstart');
      this.hammer.off('pandown panup');
      this.isBottomSheetOpen = false;
      if (this.currentBottomSheetWidget.route) {
        this.routingService.navigate(this.currentBottomSheetWidget.route, true);
      }
      this.bottomSheetRef.dismiss();
      this.setWidgetBehindBottomBar();
      this.frame.callBottomSheetClosing();
    } catch (error) {
      console.log('dismissBottomSheet error: ' + error);
    }
  }

  bottomSheetPanStart(e) {
    e.preventDefault();
    this.startPos = this.bottomSheetElement.offsetTop;
    this.startHeight = this.bottomSheetElement.clientHeight;
    this.screenHeight = window.innerHeight;
  }

  bottomSheetPanMove(e) {
    e.preventDefault();
    if (!e.isFinal) {
      const newTop = this.startPos + e.deltaY;
      let newHeight = this.startHeight;
      let topMaxPos = 0;
      if (this.dxToolBar) {
        topMaxPos = this.dxToolBar.offsetHeight;
      }
      if (e.deltaY > 0) {
        newHeight = newHeight + e.deltaY * -1;
      }
      this.newTopBottomSheet = newTop;
      this.isFirstTimeOpenBottomSheet = false;

      if (newTop >= topMaxPos) {
        this.bottomSheetElement.style.top = newTop + 'px';
        this.bottomSheetElement.style.setProperty('max-height', this.screenHeight - newTop - 64 + 'px', 'important');
        this.bottomSheetElement.style.setProperty('min-height', this.screenHeight - newTop - 64 + 'px', 'important');
        this.frame.lib.raiseViewStateChanged({
          type: ViewStateChangeArgType.BottomSheetPanelSizeChanged,
          bottomSheetPanelHeight: this.screenHeight - newTop - 64,
        });
      }
    }
  }

  private swipeCoord?: [number, number];
  private swipeTime?: number;

  swipe(e: TouchEvent, when: string): void {
    const coord: [number, number] = [e.changedTouches[0].clientX, e.changedTouches[0].clientY];
    const time = new Date().getTime();

    if (when === 'start') {
      this.swipeCoord = coord;
      this.swipeTime = time;
    } else if (when === 'end') {
      const direction = [coord[0] - this.swipeCoord[0], coord[1] - this.swipeCoord[1]];
      const duration = time - this.swipeTime;

      if (
        duration < 1000 && //
        Math.abs(direction[0]) > 30 && // Long enough
        Math.abs(direction[0]) > Math.abs(direction[1] * 3)
      ) {
        // Horizontal enough
        // const swipe = direction[0] < 0 ? 'next' : 'previous';
        // if (swipe === 'next') this.sideNav.close();
        let splitterWidth = this.splitter.elemRef.clientWidth;
        if (splitterWidth) {
          const newWidth = splitterWidth + direction[0];
          if (newWidth > 200) {
            this.splitter.width = newWidth;
          } else if (newWidth <= 0) {
            this.sideNav.close();
          }
        }
      }
    }
  }
  public trackById(index, item) {
    const id = item?.id ?? (typeof item?.getId === 'function' ? item.getId() : undefined) ?? index;
    return id ?? index;
  }

  public get isStandalone(): boolean {
    return this.frame.isStandalone;
  }

  onResizeBottomSheetElement() {
    if (this.isBottomSheetOpen && this.bottomSheetElement?.style) {
      let newTop: number;
      if (this.newTopBottomSheet > 0) {
        newTop = this.newTopBottomSheet;
      } else {
        newTop = 0;
      }

      let innerHeight: number;
      if (this.isFirstTimeOpenBottomSheet) {
        innerHeight = (window.innerHeight * this.frame.frameConfig.bottomSheetHeightPercentage) / 100;
      } else {
        innerHeight = window.innerHeight;
      }

      if (newTop + 110 > innerHeight) {
        this.dismissBottomSheet();
      } else {
        this.bottomSheetElement.style.setProperty('max-height', innerHeight - newTop - 64 + 'px', 'important');
        this.bottomSheetElement.style.setProperty('min-height', innerHeight - newTop - 64 + 'px', 'important');
        this.frame.lib.raiseViewStateChanged({
          type: ViewStateChangeArgType.BottomSheetPanelSizeChanged,
          bottomSheetPanelHeight: innerHeight - newTop - 64,
        });
      }
    }
  }

  onMenuOpen() {
    this.isMenuOpened = true;
  }

  onMenuClose() {
    this.isMenuOpened = false;
  }

  closeMenu() {
    this.menuTrigger?.closeMenu();
  }
}
